import { useState, useEffect, useRef } from 'react';
import { Building2, Home, Map, TreePine, ChevronRight, ChevronLeft, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { uploadBrochure } from '../services/api';
import { toast } from 'sonner';

const INDIA_MICROMARKETS = {
  "Alibaug": ["Agarsure", "Dhokawade", "Nagaon", "Kihim", "Zirad", "Mandwa", "Thal", "Awas", "Varsoli", "Chondhi", "Revas", "Sasawane"],
  "Mumbai 3.0": ["Panvel", "Karjat", "Khopoli", "Khalapur", "Neral", "Pushpak Nagar", "Chirle", "Mohape", "Kalundre"],
  "Lonavala": ["Lonavala", "Khandala", "Tungarli", "Aamby Valley", "Pavana", "Valvan", "Rajmachi", "Bushi"],
  "Goa": ["Assagao", "Siolim", "Anjuna", "Candolim", "Vagator", "Panjim", "Calangute", "Reis Magos", "Saligao", "Arpora", "Morjim", "Ashwem"],
};

const CONFIGURATIONS = ["1BHK", "2BHK", "3BHK", "4BHK", "5BHK", "6BHK", "7BHK", "Studio", "Penthouse"];
const COMMON_AMENITIES = ["Swimming Pool", "Clubhouse", "Gymnasium", "Children's Play Area", "Jogging Track", "Landscaped Gardens", "24/7 Security", "CCTV Surveillance", "Power Backup", "Water Supply", "Internal Roads", "Street Lighting", "Sewage Treatment", "Rainwater Harvesting", "Organic Farm", "Yoga Deck", "Amphitheatre", "Sports Court", "Cycling Track", "EV Charging", "Solar Panels", "Temple / Prayer Room", "Party Hall", "Guest Rooms"];
const VILLA_AMENITIES = ["Private Pool", "Private Garden", "Terrace", "Balcony", "Puja Room", "Staff Quarters", "Covered Parking", "Home Theater", "Wine Cellar", "Fireplace", "BBQ Area", "Outdoor Kitchen", "Jacuzzi", "Smart Home System", "Italian Marble Flooring", "Modular Kitchen", "Fully Furnished", "Semi-Furnished", "Vastu Compliant", "Sea View", "Mountain View", "Valley View", "River View", "Lake View"];
const LAND_TYPES = ["Farmhouse", "Non-Agricultural (NA)", "Farmhouse NA", "Agricultural", "CRZ Zone", "NAINA Zone", "Revenue Land"];
const INDIA_CITIES = Object.keys(INDIA_MICROMARKETS);
const INTL_PROPERTY_TYPES = ["Villa", "Apartment", "Penthouse", "Condo", "Townhouse", "Land"];

const REGION_COUNTRIES = {
  "Europe": ["Greece", "Portugal", "Spain", "Cyprus", "Malta", "UK", "France", "Italy", "Monaco", "Switzerland", "Ireland"],
  "Middle East": ["UAE", "Bahrain", "Qatar", "Oman", "Saudi Arabia", "Turkey"],
  "Asia": ["Singapore", "Thailand", "Malaysia", "Indonesia", "Philippines", "Vietnam", "Japan"],
  "Americas": ["USA", "Canada", "Mexico"],
  "Caribbean": ["Antigua & Barbuda", "Dominica", "St. Kitts & Nevis", "Grenada", "St. Lucia"],
  "Africa": ["Mauritius", "South Africa", "Egypt", "Morocco"],
  "Oceania": ["Australia", "New Zealand", "Fiji"],
};
const REGIONS = Object.keys(REGION_COUNTRIES);

const defaultForm = {
  market: '', listing_type: '', title: '', description: '',
  price_inr: '', price_usd: '', currency: 'INR',
  country: 'India', region: 'South Asia', city: '', micromarket: '',
  property_type: 'Villa',
  bedrooms: '', bathrooms: '', area_sqft: '', land_area_sqft: '',
  furnished_status: '', land_type: '',
  total_project_area: '', total_units: '', plot_area_range: '', builtup_area_range: '',
  price_range_display: '',
  configurations: [], common_amenities: [], villa_amenities: [], features: [],
  visa_eligible: false, visa_type: '', min_investment: '',
};

export default function PropertyFormWizard({ onSubmit, editData, submitting, tierAllowsAI }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [brochureUploading, setBrochureUploading] = useState(false);
  const brochureInputRef = useRef(null);

  useEffect(() => {
    if (editData) {
      const merged = { ...defaultForm, ...editData };
      merged.market = editData.market || (editData.country === 'India' ? 'india' : 'international');
      merged.listing_type = editData.listing_type || 'individual_property';
      merged.configurations = editData.configurations || [];
      merged.common_amenities = editData.common_amenities || [];
      merged.villa_amenities = editData.villa_amenities || [];
      merged.features = editData.features || [];
      merged.price_inr = editData.price_inr ? String(editData.price_inr) : '';
      merged.price_usd = editData.price_usd ? String(editData.price_usd) : '';
      merged.bedrooms = editData.bedrooms ? String(editData.bedrooms) : '';
      merged.bathrooms = editData.bathrooms ? String(editData.bathrooms) : '';
      merged.area_sqft = editData.area_sqft ? String(editData.area_sqft) : '';
      merged.land_area_sqft = editData.land_area_sqft ? String(editData.land_area_sqft) : '';
      merged.total_units = editData.total_units ? String(editData.total_units) : '';
      merged.min_investment = editData.min_investment ? String(editData.min_investment) : '';
      setForm(merged);
      setStep(1);
    }
  }, [editData]);

  const isIndia = form.market === 'india';
  const isProject = form.listing_type === 'project_villa' || form.listing_type === 'project_plot';
  const isVillaProject = form.listing_type === 'project_villa';
  const isIndividual = form.listing_type === 'individual_property';
  const micromarkets = INDIA_MICROMARKETS[form.city] || [];
  const countryOptions = REGION_COUNTRIES[form.region] || [];

  const handleBrochureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum 50MB.`);
      return;
    }
    setBrochureUploading(true);
    try {
      const result = await uploadBrochure(file);
      const ai = result.ai_extracted;
      if (ai) {
        setForm(prev => ({
          ...prev,
          title: ai.title || prev.title,
          description: ai.description || prev.description,
          property_type: ai.property_type || prev.property_type,
          bedrooms: ai.bedrooms || prev.bedrooms,
          bathrooms: ai.bathrooms || prev.bathrooms,
          area_sqft: ai.area_sqft || prev.area_sqft,
          features: ai.features?.length ? ai.features : prev.features,
          brochure_url: result.brochure_url || '',
        }));
        toast.success(`AI extracted: ${ai.title || 'property details'}`);
        if (step === 1) setStep(2);
      } else {
        toast.info('Brochure uploaded but no data could be extracted');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Brochure upload failed');
    } finally {
      setBrochureUploading(false);
    }
  };

  const toggleArrayItem = (field, item) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(item) ? prev[field].filter(i => i !== item) : [...prev[field], item]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_inr: form.price_inr ? parseFloat(form.price_inr) : 0,
        price_usd: form.price_usd ? parseFloat(form.price_usd) : 0,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        area_sqft: parseInt(form.area_sqft) || 0,
        land_area_sqft: parseInt(form.land_area_sqft) || 0,
        total_units: parseInt(form.total_units) || 0,
        min_investment: parseFloat(form.min_investment) || 0,
        visa_eligible: !isIndia && form.visa_eligible,
        country: isIndia ? 'India' : form.country,
        region: isIndia ? 'South Asia' : form.region,
        currency: isIndia ? 'INR' : 'USD',
        features: isProject ? [...(form.common_amenities || []), ...(form.villa_amenities || [])] : (form.features || []),
        status: form.status || 'live',
      };
      await onSubmit(payload);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Step Indicator */}
      <div className="flex items-center gap-2 mb-2">
        {[1, 2].map(s => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s ? 'bg-[#D4AF37] text-black' : 'bg-[#2A2F3A] text-[#A1A1AA]'}`}>{s}</div>
            <span className={`text-xs hidden sm:inline ${step >= s ? 'text-white' : 'text-[#A1A1AA]'}`}>
              {s === 1 ? 'Type & Location' : 'Details'}
            </span>
            {s < 2 && <div className={`w-10 h-0.5 ${step > s ? 'bg-[#D4AF37]' : 'bg-[#2A2F3A]'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Market, Listing Type & Location */}
      {step === 1 && (
        <div className="space-y-5">
          <div>
            <Label className="text-white mb-3 block text-sm">Is this property in India or International?</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{v:'india', l:'India', icon: <Home className="h-5 w-5" />, desc:'Indian luxury property'}, {v:'international', l:'International', icon: <Map className="h-5 w-5" />, desc:'Global investment property'}].map(opt => (
                <button type="button" key={opt.v} onClick={() => setForm({...form, market: opt.v, country: opt.v === 'india' ? 'India' : '', region: opt.v === 'india' ? 'South Asia' : '', visa_eligible: opt.v !== 'india', listing_type: opt.v === 'international' ? 'individual_property' : ''})}
                  className={`p-4 rounded-xl border text-left transition-all ${form.market === opt.v ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#2A2F3A] hover:border-[#D4AF37]/30'}`}>
                  <div className={`mb-2 ${form.market === opt.v ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'}`}>{opt.icon}</div>
                  <p className="text-white font-medium text-sm">{opt.l}</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {form.market && (
            <div>
              <Label className="text-white mb-3 block text-sm">{isIndia ? 'What are you listing?' : 'Listing Type'}</Label>
              <div className={`grid ${isIndia ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
                {isIndia ? (
                  [{v:'project_villa', l:'Villa Project', icon:<Building2 className="h-5 w-5"/>, desc:'Developer villa project'}, {v:'project_plot', l:'Plotted Project', icon:<TreePine className="h-5 w-5"/>, desc:'Developer plotted project'}, {v:'individual_property', l:'Individual Property', icon:<Home className="h-5 w-5"/>, desc:'Single property listing'}].map(opt => (
                    <button type="button" key={opt.v} onClick={() => setForm({...form, listing_type: opt.v, property_type: opt.v === 'project_plot' ? 'Plot' : 'Villa'})}
                      className={`p-4 rounded-xl border text-left transition-all ${form.listing_type === opt.v ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#2A2F3A] hover:border-[#D4AF37]/30'}`}>
                      <div className={`mb-2 ${form.listing_type === opt.v ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'}`}>{opt.icon}</div>
                      <p className="text-white font-medium text-sm">{opt.l}</p>
                      <p className="text-[#A1A1AA] text-xs mt-1">{opt.desc}</p>
                    </button>
                  ))
                ) : (
                  <button type="button" onClick={() => setForm({...form, listing_type: 'individual_property'})}
                    className="p-4 rounded-xl border border-[#D4AF37] bg-[#D4AF37]/5 text-left">
                    <p className="text-white font-medium text-sm">International Property Listing</p>
                    <p className="text-[#A1A1AA] text-xs mt-1">List a property with visa/residency program details</p>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Brochure AI Upload */}
          {form.market && form.listing_type && tierAllowsAI !== false && (
            <div className="border border-dashed border-[#2A2F3A] rounded-xl p-4 hover:border-[#D4AF37]/30 transition-colors">
              <input ref={brochureInputRef} type="file" accept=".pdf" className="hidden" onChange={handleBrochureUpload} data-testid="wizard-brochure-input" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">Have a brochure? Upload PDF for AI extraction</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">We&apos;ll automatically extract property details and descriptions</p>
                </div>
                <Button type="button" size="sm" className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 text-xs flex-shrink-0"
                  onClick={() => brochureInputRef.current?.click()} disabled={brochureUploading} data-testid="wizard-brochure-btn">
                  {brochureUploading ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Extracting...</> : <><FileSpreadsheet className="h-3 w-3 mr-1" /> Upload Brochure</>}
                </Button>
              </div>
            </div>
          )}

          {/* Location fields */}
          {form.market && form.listing_type && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#2A2F3A]">
              <div className="md:col-span-2">
                <Label className="text-white mb-2 block text-sm">{isProject ? 'Project Name *' : 'Property Title *'}</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" required placeholder={isProject ? 'e.g., Sahyadri Valley Villas' : 'e.g., 4BHK Sea-View Villa, Kihim'} data-testid="prop-title" />
              </div>
              {isIndia ? (
                <>
                  <div>
                    <Label className="text-white mb-2 block text-sm">City/Destination *</Label>
                    <Select value={form.city} onValueChange={v => setForm({...form, city: v, micromarket: ''})}>
                      <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue placeholder="Select destination" /></SelectTrigger>
                      <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{INDIA_CITIES.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Micromarket / Locality *</Label>
                    <Select value={form.micromarket} onValueChange={v => setForm({...form, micromarket: v})}>
                      <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue placeholder={form.city ? 'Select micromarket' : 'Select city first'} /></SelectTrigger>
                      <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{micromarkets.map(m => <SelectItem key={m} value={m} className="text-white">{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Region *</Label>
                    <Select value={form.region} onValueChange={v => setForm({...form, region: v, country: ''})}>
                      <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue placeholder="Select region" /></SelectTrigger>
                      <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{REGIONS.map(r => <SelectItem key={r} value={r} className="text-white">{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Country *</Label>
                    <Select value={form.country} onValueChange={v => setForm({...form, country: v})}>
                      <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue placeholder={form.region ? 'Select country' : 'Select region first'} /></SelectTrigger>
                      <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{countryOptions.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">City *</Label>
                    <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" required placeholder="e.g., Dubai" />
                  </div>
                </>
              )}
              <div className={isIndia ? '' : 'md:col-span-2'}>
                <Label className="text-white mb-2 block text-sm">{isIndia ? 'Price (\u20B9 INR) *' : 'Price (USD) *'}</Label>
                {isIndia ? (
                  <Input type="number" value={form.price_inr} onChange={e => setForm({...form, price_inr: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" required placeholder="e.g., 50000000 for 5 Cr" data-testid="prop-price" />
                ) : (
                  <Input type="number" value={form.price_usd} onChange={e => setForm({...form, price_usd: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" required placeholder="e.g., 500000" data-testid="prop-price" />
                )}
                {isIndia && form.price_inr && <p className="text-[#D4AF37] text-xs mt-1">{'\u20B9'}{(parseFloat(form.price_inr) / 10000000).toFixed(2)} Cr</p>}
              </div>
              {isProject && (
                <div>
                  <Label className="text-white mb-2 block text-sm">Price Range Display</Label>
                  <Input value={form.price_range_display} onChange={e => setForm({...form, price_range_display: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., \u20B93.5 Cr - \u20B98 Cr" />
                </div>
              )}
              <div className="md:col-span-2">
                <Label className="text-white mb-2 block text-sm">Description *</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white min-h-[80px]" required placeholder="Detailed description of the property/project..." />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Property-Specific Details */}
      {step === 2 && (
        <div className="space-y-4">
          {isProject && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block text-sm">Total Project Area</Label>
                <Input value={form.total_project_area} onChange={e => setForm({...form, total_project_area: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., 25 Acres" />
              </div>
              <div>
                <Label className="text-white mb-2 block text-sm">Total {isVillaProject ? 'Villas' : 'Plots'}</Label>
                <Input type="number" value={form.total_units} onChange={e => setForm({...form, total_units: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., 50" />
              </div>
              <div>
                <Label className="text-white mb-2 block text-sm">Plot Area Range</Label>
                <Input value={form.plot_area_range} onChange={e => setForm({...form, plot_area_range: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., 2000 - 5000 sqft" />
              </div>
              {isVillaProject && (
                <div>
                  <Label className="text-white mb-2 block text-sm">Built-up Area Range</Label>
                  <Input value={form.builtup_area_range} onChange={e => setForm({...form, builtup_area_range: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., 1500 - 3500 sqft" />
                </div>
              )}
              {isVillaProject && (
                <div className="md:col-span-2">
                  <Label className="text-white mb-2 block text-sm">Configurations</Label>
                  <div className="flex flex-wrap gap-2">
                    {CONFIGURATIONS.map(c => (
                      <button type="button" key={c} onClick={() => toggleArrayItem('configurations', c)}
                        className={`px-3 py-1 rounded-lg text-xs border transition-all ${form.configurations.includes(c) ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40' : 'bg-[#0B0C10] text-[#A1A1AA] border-[#2A2F3A] hover:border-[#D4AF37]/30'}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <Label className="text-white mb-2 block text-sm">Common Amenities & Infrastructure</Label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1">
                  {COMMON_AMENITIES.map(a => (
                    <button type="button" key={a} onClick={() => toggleArrayItem('common_amenities', a)}
                      className={`px-2.5 py-1 rounded-lg text-xs border transition-all ${form.common_amenities.includes(a) ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40' : 'bg-[#0B0C10] text-[#A1A1AA] border-[#2A2F3A] hover:border-[#D4AF37]/30'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
              {isVillaProject && (
                <div className="md:col-span-2">
                  <Label className="text-white mb-2 block text-sm">Exclusive Villa Amenities</Label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1">
                    {VILLA_AMENITIES.map(a => (
                      <button type="button" key={a} onClick={() => toggleArrayItem('villa_amenities', a)}
                        className={`px-2.5 py-1 rounded-lg text-xs border transition-all ${form.villa_amenities.includes(a) ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : 'bg-[#0B0C10] text-[#A1A1AA] border-[#2A2F3A] hover:border-emerald-500/30'}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {isIndividual && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isIndia && (
                <>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Property Type</Label>
                    <Select value={form.property_type} onValueChange={v => setForm({...form, property_type: v})}>
                      <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{["Villa", "Farmhouse", "Plot", "Bungalow", "Row House", "Penthouse"].map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Land Type</Label>
                    <Select value={form.land_type} onValueChange={v => setForm({...form, land_type: v})}>
                      <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue placeholder="Select land type" /></SelectTrigger>
                      <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{LAND_TYPES.map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {!isIndia && (
                <div>
                  <Label className="text-white mb-2 block text-sm">Property Type</Label>
                  <Select value={form.property_type} onValueChange={v => setForm({...form, property_type: v})}>
                    <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{INTL_PROPERTY_TYPES.map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-white mb-2 block text-sm">Bedrooms</Label>
                <Input type="number" value={form.bedrooms} onChange={e => setForm({...form, bedrooms: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
              </div>
              <div>
                <Label className="text-white mb-2 block text-sm">Bathrooms</Label>
                <Input type="number" value={form.bathrooms} onChange={e => setForm({...form, bathrooms: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
              </div>
              <div>
                <Label className="text-white mb-2 block text-sm">Built-up Area (sqft)</Label>
                <Input type="number" value={form.area_sqft} onChange={e => setForm({...form, area_sqft: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
              </div>
              {isIndia && (
                <div>
                  <Label className="text-white mb-2 block text-sm">Land / Plot Area (sqft)</Label>
                  <Input type="number" value={form.land_area_sqft} onChange={e => setForm({...form, land_area_sqft: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                </div>
              )}
              {isIndia && (
                <div>
                  <Label className="text-white mb-2 block text-sm">Furnished Status</Label>
                  <Select value={form.furnished_status} onValueChange={v => setForm({...form, furnished_status: v})}>
                    <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">{["Furnished", "Semi-Furnished", "Unfurnished"].map(t => <SelectItem key={t} value={t} className="text-white">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {!isIndia && (
                <>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Visa Type</Label>
                    <Input value={form.visa_type} onChange={e => setForm({...form, visa_type: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., Golden Visa" />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Min. Investment (USD)</Label>
                    <Input type="number" value={form.min_investment} onChange={e => setForm({...form, min_investment: e.target.value})} className="bg-[#0B0C10] border-[#2A2F3A] text-white" />
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <Label className="text-white mb-2 block text-sm">Amenities & Features</Label>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">
                  {(isIndia ? VILLA_AMENITIES : ["Swimming Pool","Gym","Concierge","Sea View","Balcony","Parking","Furnished","Smart Home","Security"]).map(a => (
                    <button type="button" key={a} onClick={() => toggleArrayItem('features', a)}
                      className={`px-2.5 py-1 rounded-lg text-xs border transition-all ${form.features.includes(a) ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40' : 'bg-[#0B0C10] text-[#A1A1AA] border-[#2A2F3A] hover:border-[#D4AF37]/30'}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <p className="text-[#A1A1AA] text-xs italic pt-2 border-t border-[#2A2F3A]">You can upload property images after saving using the camera button in the properties table.</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-3 border-t border-[#2A2F3A]">
        {step > 1 ? (
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStep(s => s - 1); }} className="inline-flex items-center px-4 py-2 rounded-md text-sm border border-[#2A2F3A] text-[#A1A1AA] hover:text-white transition-colors">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </button>
        ) : <div />}
        {step < 2 ? (
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStep(s => s + 1); }} disabled={!form.market || !form.listing_type || !form.title}
            className="btn-luxury inline-flex items-center px-4 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        ) : (
          <Button type="submit" className="btn-luxury" disabled={saving || submitting}>
            {saving ? 'Saving...' : 'Save Property'}
          </Button>
        )}
      </div>
    </form>
  );
}
